/** react & third-party hooks/libraries */
import type { HoverActionItem } from "@promptlayer/ui";
import {
  adaptContent,
  adaptMessages,
  HoverActionButton,
  Message as SharedMessage,
} from "@promptlayer/ui";
import { RotateCcwIcon, SquareArrowOutUpRightIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import moment from "moment";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

/** utils & constants */
import { formatLatency } from "@/components/Playground/utils";
import { cn } from "@/lib/utils";
import { Content, CONTENT_TYPES, ROLES } from "@/types";
import { formatTimestamp } from "@/utils/timestamp";
/** contexts */
import { useConfirm } from "@/context/confirm-context";
import { useUser } from "@/context/user-context";
/** components */
import { Citations } from "@/components/Citations";
import type { Message as PromptBlueprintMessage } from "@/types/prompt-blueprint";
import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { ChatMessageActions } from "@/components/Playground/Drawer/Chat/ChatMessage/ChatMessageActions";
import { ChatMessageEdit } from "@/components/Playground/Drawer/Chat/ChatMessage/ChatMessageEdit";
import { MessageAvatar } from "@/components/Playground/Drawer/Chat/ChatMessage/MessageAvatar";
/** primitives/extras */
import { AssistantMessageContentSkeleton } from "@/components/Playground/Drawer/Chat/ChatSkeleton";
import { CopyButton } from "@/components/ui/copy-button";
import { BasicTooltip } from "@/components/ui/Tooltip";
/** types */
import { ChatMessageI } from "@/components/Playground/types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ChatMessagePropsI {
  message: ChatMessageI;
  /** Full message list, used to check if an approval request has been resolved */
  messages?: ChatMessageI[];
  customAgentIcon?: ReactNode;
  hideTimestamp?: boolean;
  hideCopyButton?: boolean;
  isMinimal?: boolean;
  isStreaming?: boolean;
  onEditMessage?: (messageId: string, newContent: Content[]) => void;
  onDeleteMessage?: (messageId: string) => void;
  onReplayMessage?: (messageId: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determine whether a message's text content is editable.
 *
 * Tool messages and messages that contain tool_calls have complex structured
 * content that doesn't map to plain-text editing. Error-only messages are
 * also non-editable.
 */
const isMessageEditable = (message: ChatMessageI): boolean => {
  if ((message.tool_calls ?? []).length > 0) {
    return false;
  }
  if (message.error) {
    return false;
  }
  return Array.isArray(message.content) && message.content.some(c => c.type === CONTENT_TYPES.TEXT);
};

// ---------------------------------------------------------------------------
// LatencyCostRow — bottom metadata shown on hover below assistant bubbles
// ---------------------------------------------------------------------------

interface LatencyCostRowProps {
  message: ChatMessageI;
  activeWorkspaceId: string | undefined;
  isMinimal?: boolean;
}

const LatencyCostRow = ({ message, activeWorkspaceId, isMinimal }: LatencyCostRowProps) => (
  <div className="flex items-center justify-between gap-2 text-[10px] text-gray-400">
    <div className="flex items-center gap-2">
      {!!message.latency_ms && (
        <BasicTooltip content="Latency" side="bottom">
          <span>{formatLatency(moment.duration(message.latency_ms, "ms"))}</span>
        </BasicTooltip>
      )}
      {!!message.cost && (
        <BasicTooltip content="Cost" side="bottom">
          <span>${message.cost.toFixed(5)}</span>
        </BasicTooltip>
      )}
    </div>
    {!!message.request_id && (
      <BasicTooltip content="Open in new tab to view the request" side="bottom">
        <Link
          to={`/workspace/${activeWorkspaceId}/request/${message.request_id}`}
          target="_blank"
          className="flex items-center gap-1 text-gray-400 transition-colors hover:text-gray-600"
        >
          {!isMinimal && <span>Go to Request</span>}
          <SquareArrowOutUpRightIcon className="h-3 w-3" />
        </Link>
      </BasicTooltip>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChatMessage = observer(
  ({
    message,
    messages: allMessages,
    isMinimal,
    isStreaming,
    onEditMessage,
    onDeleteMessage,
    onReplayMessage,
    customAgentIcon,
    hideTimestamp,
    hideCopyButton,
  }: ChatMessagePropsI) => {
    const { activeWorkspaceId } = useUser();
    const { confirm } = useConfirm();
    const hasAnimated = useRef(false);
    const [isEditing, setIsEditing] = useState(false);

    // ---- Derived state ----
    const isUser = message.role === ROLES.USER;
    const isTool = message.role === ROLES.TOOL;
    const isRightAligned = isUser || isTool;
    const isError = !!message.error;

    // ---- Adapt content to shared types ----
    const contentBlocks = useMemo(
      () => (Array.isArray(message.content) ? adaptContent(message.content) : []),
      [message.content],
    );

    // Flatten text for the copy button
    const textContent = useMemo(
      () =>
        contentBlocks
          .filter((b): b is { type: "text"; text: string } => b.type === "text")
          .map(b => b.text)
          .join(" "),
      [contentBlocks],
    );

    const toolCalls = message.tool_calls ?? [];

    const hasBottomMetadata =
      !isRightAligned && (!!message.latency_ms || !!message.cost || !!message.request_id);

    // ---- Adapt all messages for cross-message approval resolution ----
    const allDisplayMessages = useMemo(
      () => (allMessages ? adaptMessages(allMessages) : undefined),
      [allMessages],
    );

    // ---- Timestamp ----
    const createdAt = formatTimestamp(message.created_at);
    const hasValidDate = createdAt !== "Invalid date";

    // ---- Edit / Delete ----
    const canEdit = isMessageEditable(message);
    const actionsDisabled = !!isStreaming;
    const hasActionHandlers = !!onEditMessage || !!onDeleteMessage || !!onReplayMessage;

    const handleReplay = useCallback(() => {
      onReplayMessage?.(message.id);
    }, [message.id, onReplayMessage]);

    const handleStartEdit = useCallback(() => setIsEditing(true), []);
    const handleCancelEdit = useCallback(() => setIsEditing(false), []);
    const handleSaveEdit = useCallback(
      (newContent: Content[]) => {
        onEditMessage?.(message.id, newContent);
        setIsEditing(false);
      },
      [message.id, onEditMessage],
    );
    const handleDelete = useCallback(async () => {
      const isConfirmed = await confirm({
        title: "Delete message",
        message:
          "Are you sure you want to delete this message? This will remove the entire conversation turn and cannot be undone.",
        confirmText: "Delete",
        cancelText: "Cancel",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
      });
      if (isConfirmed) {
        onDeleteMessage?.(message.id);
      }
    }, [message.id, onDeleteMessage, confirm]);

    // ---- Animation guard ----
    const shouldAnimate = !hasAnimated.current;
    if (shouldAnimate) {
      hasAnimated.current = true;
    }

    // ---- Bubble style overrides ----
    // MCP approval responses get their own colour — override the default.
    const mcpApprovalResponses = useMemo(
      () =>
        Array.isArray(message.content)
          ? message.content.filter(c => c.type === "mcp_approval_response")
          : [],
      [message.content],
    );
    const approvalResponse =
      mcpApprovalResponses.length > 0 ? (mcpApprovalResponses[0] as { approve: boolean }) : null;

    let bubbleClassName: string | undefined;
    if (approvalResponse) {
      bubbleClassName = approvalResponse.approve
        ? "border border-green-200 bg-green-50 text-green-700"
        : "border border-red-200 bg-red-50 text-red-700";
    }

    // ---- Extra hover actions (app-specific: replay, copy, edit/delete) ----
    const extraHoverActions: HoverActionItem[] = isEditing
      ? []
      : [
          // Replay button — user / tool messages only
          ...(isRightAligned && onReplayMessage
            ? [
                {
                  key: "replay",
                  node: (
                    <BasicTooltip
                      content="Replay from here"
                      side={isRightAligned ? "left" : "right"}
                    >
                      <HoverActionButton onClick={handleReplay} aria-label="Replay from here">
                        <RotateCcwIcon className="h-3.5 w-3.5" />
                      </HoverActionButton>
                    </BasicTooltip>
                  ),
                },
              ]
            : []),

          // Copy button (app-specific CopyButton with tooltip styling)
          ...(textContent && !hideCopyButton
            ? [
                {
                  key: "copy",
                  node: (
                    <CopyButton
                      text={textContent}
                      variant="ghost"
                      iconSize="h-3.5 w-3.5"
                      className="h-6 w-6 rounded-md p-0 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                    />
                  ),
                },
              ]
            : []),

          // Actions dropdown (edit / delete)
          ...(hasActionHandlers
            ? [
                {
                  key: "actions",
                  node: (
                    <ChatMessageActions
                      isUser={isRightAligned}
                      canEdit={canEdit}
                      onEdit={handleStartEdit}
                      onDelete={handleDelete}
                      disabled={actionsDisabled}
                    />
                  ),
                },
              ]
            : []),
        ];

    // ---- Bottom metadata (latency/cost) + citations (always visible) ----
    // Show pill for assistant (text annotations) and tool (web_search_tool_result) messages
    const hasAnnotations =
      Array.isArray(message.content) &&
      message.content.some(c => {
        if (c.type === "text") {
          const anns = (c as { annotations?: unknown[] }).annotations;
          return Array.isArray(anns) && anns.length > 0;
        }
        if (c.type === "web_search_tool_result") {
          const items = (c as { content?: unknown[] }).content;
          return Array.isArray(items) && items.length > 0;
        }
        return false;
      });
    const citationsMessage: PromptBlueprintMessage = Array.isArray(message.content)
      ? { role: "assistant", content: message.content, template_format: "f-string" }
      : { role: "assistant", content: [], template_format: "f-string" };
    const bottomMetadata = hasBottomMetadata ? (
      <LatencyCostRow
        message={message}
        activeWorkspaceId={activeWorkspaceId?.toString()}
        isMinimal={isMinimal}
      />
    ) : undefined;

    // ---- Bubble variant ----
    let variant: "error" | "editing" | "default" = "default";
    if (isError) {
      variant = "error";
    } else if (isEditing) {
      variant = "editing";
    }

    return (
      <div className="flex flex-col">
        <SharedMessage
          id={message.id}
          role={message.role as "user" | "assistant" | "tool"}
          content={contentBlocks}
          toolCalls={toolCalls}
          name={message.name}
          toolCallId={message.tool_call_id}
          isStreaming={isStreaming}
          error={isError ? message.error : undefined}
          allMessages={allDisplayMessages}
          variant={variant}
          bubbleClassName={bubbleClassName}
          animateKey={message.id}
          disableAnimation={!shouldAnimate}
          timestamp={hasValidDate && !hideTimestamp ? createdAt : undefined}
          avatar={
            !isMinimal && !isRightAligned ? (
              <MessageAvatar customAgentIcon={customAgentIcon} />
            ) : undefined
          }
          customAgentIcon={customAgentIcon}
          extraHoverActions={extraHoverActions}
          hideCopyButton={hideCopyButton || extraHoverActions.some(a => a.key === "copy")}
          bottomMetadata={bottomMetadata}
          maxWidthClass="max-w-[85%]"
          isEditing={isEditing}
          renderEditMode={() => (
            <ChatMessageEdit
              content={message.content}
              isUser={isUser}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
          )}
          streamingSkeleton={
            !isError ? (
              <AssistantMessageContentSkeleton classNames={["min-w-[140px]", "min-w-[100px]"]} />
            ) : undefined
          }
        />
        {hasAnnotations && (
          <div className={cn("mt-1 flex gap-2 px-2", isRightAligned && "flex-row-reverse")}>
            <div className={cn("w-7 shrink-0", (isMinimal || isRightAligned) && "hidden")} />
            <div className="min-w-0 flex-1">
              <Citations messages={[citationsMessage]} compact />
            </div>
          </div>
        )}
      </div>
    );
  },
);
